import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/ApiService.js';
import { LOGIN_FORM_TYPES } from '../../appContants.js';

/**
 * Handles password-based authentication flows:
 * - Standard password login
 * - New password challenge (first-time / forced change)
 * - Redirects unconfirmed users to signup verification
 */
const usePasswordAuth = ({ email, onChallengeReceived, onDeviceTrusted, notify }) => {
  const navigate = useNavigate();
  const [cognitoUser, setCognitoUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formCredentials, setFormCredentials] = useState(null);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handlePasswordLogin = async ({ password }, resetForm) => {
    const user = await ApiService.logIn({ email, password });
    if (!user) return;

    setFormCredentials({ email, password });
    setCognitoUser(user);
    resetForm({ email, password: '' });

    if (user?.challengeName === LOGIN_FORM_TYPES.CUSTOM_CHALLENGE) {
      onChallengeReceived?.('OTP');
    } else if (!user?.challengeName && user?.signInUserSession) {
      onDeviceTrusted?.(user);
    }
  };

  const handleUnconfirmedUser = async () => {
    await ApiService.forgetPasswordResend(email);
    notify.success({
      message: 'Email verification required',
      description:
        'We have sent a verification code to your email. Please confirm your account to continue.',
    });
    navigate('/signup', { state: { fromLogin: true, email } });
  };

  const handleNewPasswordSubmit = async (data, resetForm) => {
    const user = await ApiService.updateNewUserPassword(cognitoUser, data);
    if (!user) {
      notify.error({ message: 'Error', description: 'Something is wrong!!' });
      return;
    }
    setCognitoUser(user);
    resetForm();
    if (user?.challengeName === LOGIN_FORM_TYPES.CUSTOM_CHALLENGE) {
      onChallengeReceived?.('OTP');
    } else if (!user?.challengeName && user?.signInUserSession) {
      onDeviceTrusted?.(user);
    }
  };

  const resetAuth = () => {
    setCognitoUser(null);
    setFormCredentials(null);
    setShowPassword(false);
  };

  return {
    cognitoUser,
    setCognitoUser,
    showPassword,
    formCredentials,
    togglePasswordVisibility,
    handlePasswordLogin,
    handleUnconfirmedUser,
    handleNewPasswordSubmit,
    resetAuth,
  };
};

export default usePasswordAuth;
